import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, LogOut, Users, FileText, Terminal, 
  Eye, Activity, TrendingUp, Save, Trash2, Edit, Plus,
  XCircle, Monitor, Cpu, HardDrive, Newspaper, BarChart3,
  Sun, Moon, Download, ExternalLink, Globe, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/admin`;

// Theme colors
const CHART_COLORS = ['#2A9D8F', '#E9C46A', '#F4A261', '#E76F51', '#264653'];

export default function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Data states
  const [stats, setStats] = useState({});
  const [accessLogs, setAccessLogs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [questionarios, setQuestionarios] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  
  // Analytics data
  const [accessByDay, setAccessByDay] = useState([]);
  const [questionariosByWeek, setQuestionariosByWeek] = useState([]);
  const [newslettersByMonth, setNewslettersByMonth] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);

  // Form states
  const [noteForm, setNoteForm] = useState({ title: '', content: '' });
  const [newsletterForm, setNewsletterForm] = useState({ 
    title: '', content: '', category: 'Psicologia', source_url: '', published: true 
  });
  const [editingNote, setEditingNote] = useState(null);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [importCategory, setImportCategory] = useState('Psicologia');
  const [importing, setImporting] = useState(false);
  const [showOriginal, setShowOriginal] = useState({});

  // Apply theme
  useEffect(() => {
    localStorage.setItem('adminDarkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  const loadDataForTab = useCallback(async (tab) => {
    const currentToken = token || localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${currentToken}` } };
    
    try {
      switch (tab) {
        case 'dashboard':
          const dashResponse = await axios.get(`${API}/dashboard/stats`, config);
          setStats(dashResponse.data);
          break;
        case 'access':
          const accessResponse = await axios.get(`${API}/access-logs`, config);
          setAccessLogs(accessResponse.data);
          break;
        case 'notes':
          const notesResponse = await axios.get(`${API}/notes`, config);
          setNotes(notesResponse.data);
          break;
        case 'questionarios':
          const qResponse = await axios.get(`${API}/questionarios`, config);
          setQuestionarios(qResponse.data);
          break;
        case 'newsletter':
          const nlResponse = await axios.get(`${API}/newsletter`, config);
          setNewsletters(nlResponse.data);
          break;
        case 'analytics':
          const [accessData, qData, nlData, catData] = await Promise.all([
            axios.get(`${API}/analytics/access-by-day?days=30`, config),
            axios.get(`${API}/analytics/questionarios-by-week?weeks=12`, config),
            axios.get(`${API}/analytics/newsletters-by-month?months=6`, config),
            axios.get(`${API}/analytics/category-distribution`, config)
          ]);
          setAccessByDay(accessData.data);
          setQuestionariosByWeek(qData.data);
          setNewslettersByMonth(nlData.data);
          setCategoryDistribution(catData.data);
          break;
        case 'terminal':
          const [logsResponse, statusResponse] = await Promise.all([
            axios.get(`${API}/system/logs`, config),
            axios.get(`${API}/system/status`, config)
          ]);
          setSystemLogs(logsResponse.data);
          setSystemStatus(statusResponse.data);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Failed to load data for ${tab}:`, err);
      if (err.response?.status === 401) {
        // Token expired - clear and redirect to login
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        setToken('');
      }
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      loadDataForTab(activeTab);
    }
  }, [activeTab, isAuthenticated, token, loadDataForTab]);

  const axiosConfig = () => {
    const currentToken = token || localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${currentToken}` } };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, { username, password });
      const { token: newToken } = response.data;
      
      localStorage.setItem('adminToken', newToken);
      setToken(newToken);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/logout`, {}, axiosConfig());
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setToken('');
    setUsername('');
    setPassword('');
  };

  // CRUD Operations for Notes
  const createNote = async () => {
    if (!noteForm.title || !noteForm.content) return;
    
    try {
      await axios.post(`${API}/notes`, noteForm, axiosConfig());
      setNoteForm({ title: '', content: '' });
      loadDataForTab('notes');
    } catch (err) {
      console.error('Failed to create note:', err);
    }
  };

  const updateNote = async (id) => {
    try {
      await axios.put(`${API}/notes/${id}`, noteForm, axiosConfig());
      setEditingNote(null);
      setNoteForm({ title: '', content: '' });
      loadDataForTab('notes');
    } catch (err) {
      console.error('Failed to update note:', err);
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta anotação?')) return;
    
    try {
      await axios.delete(`${API}/notes/${id}`, axiosConfig());
      loadDataForTab('notes');
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  // CRUD Operations for Newsletter
  const createNewsletter = async () => {
    if (!newsletterForm.title || !newsletterForm.content || !newsletterForm.category) return;
    
    try {
      await axios.post(`${API}/newsletter`, newsletterForm, axiosConfig());
      setNewsletterForm({ title: '', content: '', category: 'Psicologia', source_url: '', published: true });
      loadDataForTab('newsletter');
    } catch (err) {
      console.error('Failed to create newsletter:', err);
    }
  };

  const updateNewsletter = async (id) => {
    try {
      await axios.put(`${API}/newsletter/${id}`, newsletterForm, axiosConfig());
      setEditingNewsletter(null);
      setNewsletterForm({ title: '', content: '', category: 'Psicologia', source_url: '', published: true });
      loadDataForTab('newsletter');
    } catch (err) {
      console.error('Failed to update newsletter:', err);
    }
  };

  const deleteNewsletter = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta newsletter?')) return;
    
    try {
      await axios.delete(`${API}/newsletter/${id}`, axiosConfig());
      loadDataForTab('newsletter');
    } catch (err) {
      console.error('Failed to delete newsletter:', err);
    }
  };

  const importNewsletters = async () => {
    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/newsletter/import?category=${importCategory}&translate=true`,
        {},
        axiosConfig()
      );
      alert(`Importados ${response.data.imported} artigos com sucesso!`);
      loadDataForTab('newsletter');
    } catch (err) {
      console.error('Failed to import newsletters:', err);
      alert('Erro ao importar notícias: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const deleteQuestionario = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este questionário?')) return;
    
    try {
      await axios.delete(`${API}/questionarios/${id}`, axiosConfig());
      loadDataForTab('questionarios');
    } catch (err) {
      console.error('Failed to delete questionário:', err);
    }
  };

  const toggleShowOriginal = (id) => {
    setShowOriginal(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Theme classes
  const themeClasses = {
    bg: darkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-blue-50',
    card: darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-[#c5a467]/20',
    cardHover: darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50',
    text: darkMode ? 'text-slate-100' : 'text-[#0a1a2f]',
    textMuted: darkMode ? 'text-slate-400' : 'text-stone-600',
    input: darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-[#c5a467]/30 text-[#0a1a2f]',
    tableRow: darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-stone-200 hover:bg-slate-50'
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1a2f] via-[#0d2240] to-[#0a1a2f] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border-2 border-[#c5a467] shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="bg-[#c5a467] p-4 rounded-full">
                <Shield className="w-12 h-12 text-[#0a1a2f]" />
              </div>
            </div>
            
            <h1 className="text-3xl font-bold text-white text-center mb-2">Painel Administrativo</h1>
            <p className="text-center text-stone-300 mb-8">Acesso Restrito</p>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-white px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-[#c5a467] font-semibold mb-2">Usuário</label>
                <input
                  type="text"
                  data-testid="login-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border-2 border-[#c5a467]/30 rounded-xl text-white focus:border-[#c5a467] focus:outline-none"
                  placeholder="Digite seu usuário"
                  required
                />
              </div>
              
              <div>
                <label className="block text-[#c5a467] font-semibold mb-2">Senha</label>
                <input
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border-2 border-[#c5a467]/30 rounded-xl text-white focus:border-[#c5a467] focus:outline-none"
                  placeholder="Digite sua senha"
                  required
                />
              </div>
              
              <button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#c5a467] to-[#d4b574] text-[#0a1a2f] py-3 rounded-xl font-bold hover:from-[#d4b574] hover:to-[#e3c481] transition-all shadow-lg disabled:opacity-50"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel
  return (
    <div className={`min-h-screen ${themeClasses.bg} transition-colors duration-300`}>
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0a1a2f] to-[#0d2240] text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#c5a467]" />
            <h1 className="text-2xl font-bold">Painel Administrativo</h1>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              data-testid="theme-toggle"
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
              title={darkMode ? 'Modo Claro' : 'Modo Escuro'}
            >
              {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-300" />}
            </button>
            
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 px-4 py-2 rounded-lg transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Navigation Tabs */}
        <div className={`${themeClasses.card} rounded-2xl shadow-lg p-2 mb-6 overflow-x-auto border-2`}>
          <div className="flex gap-2 min-w-max">
            {[
              { id: 'dashboard', icon: Activity, label: 'Dashboard' },
              { id: 'newsletter', icon: Newspaper, label: 'Newsletter' },
              { id: 'analytics', icon: BarChart3, label: 'Analytics' },
              { id: 'access', icon: Eye, label: 'Acessos' },
              { id: 'notes', icon: FileText, label: 'Anotações' },
              { id: 'questionarios', icon: Users, label: 'Questionários' },
              { id: 'terminal', icon: Terminal, label: 'Terminal' }
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#0a1a2f] to-[#0d2240] text-white'
                    : `${themeClasses.textMuted} ${themeClasses.cardHover}`
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className={`${themeClasses.card} rounded-2xl shadow-lg p-6 border-2`}>
          
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Visão Geral</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: 'Total Questionários', value: stats.total_questionarios || 0, icon: Users, color: 'blue' },
                  { label: 'Hoje', value: stats.today_questionarios || 0, icon: TrendingUp, color: 'green' },
                  { label: 'Total Acessos', value: stats.total_access_logs || 0, icon: Eye, color: 'purple' },
                  { label: 'Newsletters', value: stats.total_newsletters || 0, icon: Newspaper, color: 'orange' }
                ].map((stat, index) => (
                  <div key={index} className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-center justify-between mb-2">
                      <stat.icon className={`w-8 h-8 ${themeClasses.text}`} />
                      <span className={`text-3xl font-bold ${themeClasses.text}`}>{stat.value}</span>
                    </div>
                    <p className={`${themeClasses.textMuted} font-semibold`}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Newsletter */}
          {activeTab === 'newsletter' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-2xl font-bold ${themeClasses.text}`}>Gerenciador de Newsletter</h2>
                
                {/* Import Section */}
                <div className="flex items-center gap-3">
                  <select
                    value={importCategory}
                    onChange={(e) => setImportCategory(e.target.value)}
                    data-testid="import-category"
                    className={`px-4 py-2 rounded-lg border-2 ${themeClasses.input} focus:border-[#c5a467] focus:outline-none`}
                  >
                    <option value="Psicologia">Psicologia</option>
                    <option value="Família">Família</option>
                    <option value="Adolescência">Adolescência</option>
                  </select>
                  <button
                    onClick={importNewsletters}
                    disabled={importing}
                    data-testid="import-btn"
                    className="flex items-center gap-2 bg-gradient-to-r from-[#2A9D8F] to-[#264653] text-white px-4 py-2 rounded-lg font-bold hover:opacity-90 disabled:opacity-50"
                  >
                    {importing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                    {importing ? 'Importando...' : 'Importar Notícias'}
                  </button>
                </div>
              </div>
              
              {/* Newsletter Form */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20 mb-6`}>
                <h3 className={`font-bold ${themeClasses.text} mb-4`}>
                  {editingNewsletter ? 'Editar Newsletter' : 'Nova Newsletter'}
                </h3>
                <input
                  type="text"
                  placeholder="Título"
                  data-testid="newsletter-title"
                  value={newsletterForm.title}
                  onChange={(e) => setNewsletterForm({ ...newsletterForm, title: e.target.value })}
                  className={`w-full px-4 py-3 border-2 rounded-xl mb-3 focus:border-[#c5a467] focus:outline-none ${themeClasses.input}`}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <select
                    value={newsletterForm.category}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, category: e.target.value })}
                    data-testid="newsletter-category"
                    className={`px-4 py-3 border-2 rounded-xl focus:border-[#c5a467] focus:outline-none ${themeClasses.input}`}
                  >
                    <option value="Psicologia">Psicologia</option>
                    <option value="Família">Família</option>
                    <option value="Adolescência">Adolescência</option>
                  </select>
                  <input
                    type="url"
                    placeholder="URL da Fonte (opcional)"
                    data-testid="newsletter-source"
                    value={newsletterForm.source_url}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, source_url: e.target.value })}
                    className={`px-4 py-3 border-2 rounded-xl focus:border-[#c5a467] focus:outline-none ${themeClasses.input}`}
                  />
                </div>
                <textarea
                  placeholder="Conteúdo/Resumo"
                  data-testid="newsletter-content"
                  value={newsletterForm.content}
                  onChange={(e) => setNewsletterForm({ ...newsletterForm, content: e.target.value })}
                  rows="4"
                  className={`w-full px-4 py-3 border-2 rounded-xl mb-3 focus:border-[#c5a467] focus:outline-none resize-none ${themeClasses.input}`}
                />
                <label className={`flex items-center gap-2 mb-4 ${themeClasses.textMuted}`}>
                  <input
                    type="checkbox"
                    checked={newsletterForm.published}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, published: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="font-semibold">Publicar imediatamente</span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => editingNewsletter ? updateNewsletter(editingNewsletter) : createNewsletter()}
                    data-testid="newsletter-submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-[#0a1a2f] to-[#0d2240] text-white px-6 py-3 rounded-xl font-bold hover:opacity-90"
                  >
                    <Save className="w-5 h-5" />
                    {editingNewsletter ? 'Atualizar' : 'Salvar'}
                  </button>
                  {editingNewsletter && (
                    <button
                      onClick={() => {
                        setEditingNewsletter(null);
                        setNewsletterForm({ title: '', content: '', category: 'Psicologia', source_url: '', published: true });
                      }}
                      className={`px-6 py-3 ${darkMode ? 'bg-slate-600 text-white' : 'bg-stone-200 text-stone-700'} rounded-xl font-bold hover:opacity-80`}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>

              {/* Newsletter List */}
              <div className="space-y-4">
                {newsletters.map(post => (
                  <div key={post.id} className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h4 className={`text-xl font-bold ${themeClasses.text}`}>
                            {showOriginal[post.id] && post.original_title ? post.original_title : post.title}
                          </h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            post.published ? 'bg-green-500 text-white' : 'bg-stone-300 text-stone-700'
                          }`}>
                            {post.published ? 'Publicado' : 'Rascunho'}
                          </span>
                          {post.imported && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500 text-white">
                              Importado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#c5a467] font-semibold mb-2">
                          Categoria: {post.category}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {post.original_title && (
                          <button
                            onClick={() => toggleShowOriginal(post.id)}
                            className={`p-2 ${darkMode ? 'bg-slate-600' : 'bg-purple-500'} text-white rounded-lg hover:opacity-80`}
                            title={showOriginal[post.id] ? 'Ver Traduzido' : 'Ver Original'}
                          >
                            <Globe className="w-4 h-4" />
                          </button>
                        )}
                        {post.source_url && (
                          <a
                            href={post.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`p-2 ${darkMode ? 'bg-slate-600' : 'bg-green-500'} text-white rounded-lg hover:opacity-80`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={() => {
                            setEditingNewsletter(post.id);
                            setNewsletterForm({ 
                              title: post.title, 
                              content: post.content,
                              category: post.category,
                              source_url: post.source_url || '',
                              published: post.published 
                            });
                          }}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteNewsletter(post.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className={`${themeClasses.textMuted} whitespace-pre-wrap mb-3 line-clamp-3`}>
                      {showOriginal[post.id] && post.original_content ? post.original_content : post.content}
                    </p>
                    <p className={`text-sm ${themeClasses.textMuted}`}>
                      Criado em: {new Date(post.created_at).toLocaleString('pt-BR')} por {post.author}
                    </p>
                  </div>
                ))}
                {newsletters.length === 0 && (
                  <p className={`text-center ${themeClasses.textMuted} py-8`}>
                    Nenhuma newsletter encontrada. Crie uma nova ou importe notícias.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Analytics */}
          {activeTab === 'analytics' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Analytics</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Access by Day */}
                <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                  <h3 className={`font-bold ${themeClasses.text} mb-4`}>Acessos por Dia (últimos 30 dias)</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={accessByDay}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#475569' : '#e2e8f0'} />
                      <XAxis 
                        dataKey="date" 
                        stroke={darkMode ? '#94a3b8' : '#64748b'}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#1e293b' : '#fff',
                          border: '1px solid #c5a467',
                          borderRadius: '8px'
                        }}
                      />
                      <Area type="monotone" dataKey="count" stroke="#2A9D8F" fill="#2A9D8F" fillOpacity={0.3} name="Acessos" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Questionarios by Week */}
                <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                  <h3 className={`font-bold ${themeClasses.text} mb-4`}>Questionários por Semana</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={questionariosByWeek}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#475569' : '#e2e8f0'} />
                      <XAxis 
                        dataKey="week" 
                        stroke={darkMode ? '#94a3b8' : '#64748b'}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#1e293b' : '#fff',
                          border: '1px solid #c5a467',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" fill="#E9C46A" name="Questionários" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Newsletters by Month */}
                <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                  <h3 className={`font-bold ${themeClasses.text} mb-4`}>Newsletters por Mês</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={newslettersByMonth}>
                      <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#475569' : '#e2e8f0'} />
                      <XAxis 
                        dataKey="month" 
                        stroke={darkMode ? '#94a3b8' : '#64748b'}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#1e293b' : '#fff',
                          border: '1px solid #c5a467',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" fill="#F4A261" name="Newsletters" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Category Distribution */}
                <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                  <h3 className={`font-bold ${themeClasses.text} mb-4`}>Distribuição por Categoria</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={categoryDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="category"
                      >
                        {categoryDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: darkMode ? '#1e293b' : '#fff',
                          border: '1px solid #c5a467',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Access Logs */}
          {activeTab === 'access' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Monitoramento de Acessos</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[#c5a467]">
                      <th className={`text-left p-3 font-semibold ${themeClasses.text}`}>IP</th>
                      <th className={`text-left p-3 font-semibold ${themeClasses.text}`}>Caminho</th>
                      <th className={`text-left p-3 font-semibold ${themeClasses.text}`}>User Agent</th>
                      <th className={`text-left p-3 font-semibold ${themeClasses.text}`}>Data/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessLogs.map((log, index) => (
                      <tr key={index} className={`border-b ${themeClasses.tableRow}`}>
                        <td className={`p-3 font-mono text-sm ${themeClasses.textMuted}`}>{log.ip}</td>
                        <td className={`p-3 ${themeClasses.textMuted}`}>{log.path}</td>
                        <td className={`p-3 text-sm truncate max-w-xs ${themeClasses.textMuted}`}>{log.user_agent}</td>
                        <td className={`p-3 text-sm ${themeClasses.textMuted}`}>{new Date(log.timestamp).toLocaleString('pt-BR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {activeTab === 'notes' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Anotações do Administrador</h2>
              
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20 mb-6`}>
                <h3 className={`font-bold ${themeClasses.text} mb-4`}>
                  {editingNote ? 'Editar Anotação' : 'Nova Anotação'}
                </h3>
                <input
                  type="text"
                  placeholder="Título"
                  data-testid="note-title"
                  value={noteForm.title}
                  onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  className={`w-full px-4 py-3 border-2 rounded-xl mb-3 focus:border-[#c5a467] focus:outline-none ${themeClasses.input}`}
                />
                <textarea
                  placeholder="Conteúdo"
                  data-testid="note-content"
                  value={noteForm.content}
                  onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  rows="4"
                  className={`w-full px-4 py-3 border-2 rounded-xl mb-3 focus:border-[#c5a467] focus:outline-none resize-none ${themeClasses.input}`}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => editingNote ? updateNote(editingNote) : createNote()}
                    data-testid="note-submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-[#0a1a2f] to-[#0d2240] text-white px-6 py-3 rounded-xl font-bold hover:opacity-90"
                  >
                    <Save className="w-5 h-5" />
                    {editingNote ? 'Atualizar' : 'Salvar'}
                  </button>
                  {editingNote && (
                    <button
                      onClick={() => {
                        setEditingNote(null);
                        setNoteForm({ title: '', content: '' });
                      }}
                      className={`px-6 py-3 ${darkMode ? 'bg-slate-600 text-white' : 'bg-stone-200 text-stone-700'} rounded-xl font-bold hover:opacity-80`}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {notes.map(note => (
                  <div key={note.id} className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-start justify-between mb-3">
                      <h4 className={`text-xl font-bold ${themeClasses.text}`}>{note.title}</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingNote(note.id);
                            setNoteForm({ title: note.title, content: note.content });
                          }}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className={`${themeClasses.textMuted} whitespace-pre-wrap mb-3`}>{note.content}</p>
                    <p className={`text-sm ${themeClasses.textMuted}`}>
                      Criado em: {new Date(note.timestamp).toLocaleString('pt-BR')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Questionários */}
          {activeTab === 'questionarios' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Questionários Recebidos</h2>
              <div className="space-y-4">
                {questionarios.map(q => (
                  <div key={q.id} className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className={`text-xl font-bold ${themeClasses.text}`}>{q.nome}</h4>
                        <p className={themeClasses.textMuted}>{q.email} | {q.telefone}</p>
                      </div>
                      <button
                        onClick={() => deleteQuestionario(q.id)}
                        className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold text-[#c5a467]">Idade do Adolescente:</p>
                        <p className={themeClasses.text}>{q.idadeAdolescente} anos</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#c5a467]">Recebido em:</p>
                        <p className={themeClasses.text}>{new Date(q.timestamp).toLocaleString('pt-BR')}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#c5a467] mb-2">Descrição:</p>
                      <p className={`${themeClasses.textMuted} whitespace-pre-wrap ${darkMode ? 'bg-slate-800' : 'bg-white'} p-4 rounded-lg`}>{q.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Terminal */}
          {activeTab === 'terminal' && (
            <div>
              <h2 className={`text-2xl font-bold ${themeClasses.text} mb-6`}>Terminal Avançado</h2>
              
              {systemStatus && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-center justify-between mb-4">
                      <Cpu className={`w-8 h-8 ${themeClasses.text}`} />
                      <span className={`text-3xl font-bold ${themeClasses.text}`}>{systemStatus.cpu_percent}%</span>
                    </div>
                    <p className={`${themeClasses.textMuted} font-semibold`}>CPU Usage</p>
                    <p className={`text-sm ${themeClasses.textMuted} mt-2`}>{systemStatus.cpu_count} cores</p>
                  </div>

                  <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-center justify-between mb-4">
                      <Monitor className={`w-8 h-8 ${themeClasses.text}`} />
                      <span className={`text-3xl font-bold ${themeClasses.text}`}>{systemStatus.memory?.percent}%</span>
                    </div>
                    <p className={`${themeClasses.textMuted} font-semibold`}>Memory Usage</p>
                    <p className={`text-sm ${themeClasses.textMuted} mt-2`}>
                      {(systemStatus.memory?.used / 1024 / 1024 / 1024).toFixed(2)} GB usado
                    </p>
                  </div>

                  <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-slate-50 to-blue-50'} p-6 rounded-xl border-2 border-[#c5a467]/20`}>
                    <div className="flex items-center justify-between mb-4">
                      <HardDrive className={`w-8 h-8 ${themeClasses.text}`} />
                      <span className={`text-3xl font-bold ${themeClasses.text}`}>{systemStatus.disk?.percent}%</span>
                    </div>
                    <p className={`${themeClasses.textMuted} font-semibold`}>Disk Usage</p>
                    <p className={`text-sm ${themeClasses.textMuted} mt-2`}>
                      {(systemStatus.disk?.free / 1024 / 1024 / 1024).toFixed(2)} GB livre
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-black rounded-xl p-6 font-mono text-sm">
                <div className="text-green-400 mb-4">System Logs (Latest 50)</div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {systemLogs.slice(0, 50).map((log, index) => (
                    <div key={index} className="text-gray-300">
                      <span className="text-yellow-400">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                      {' '}
                      <span className="text-blue-400">[{log.type}]</span>
                      {' '}
                      {log.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
